import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Password } from 'primereact/password';

interface NewPasswordDialogProps {
  visible: boolean;
  onSubmit: (newPassword: string) => void;
  onCancel: () => void;
}

export const NewPasswordDialog: React.FC<NewPasswordDialogProps> = ({
  visible,
  onSubmit,
  onCancel,
}) => {
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [errors, setErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const validatePasswords = (): boolean => {
    const newErrors: { newPassword?: string; confirmPassword?: string } = {};

    if (!newPassword || newPassword.length < 6) {
      newErrors.newPassword = 'הסיסמא חייבת להכיל לפחות 6 תווים';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'נא לאמת את הסיסמא';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'הסיסמאות אינן תואמות';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validatePasswords()) {
      onSubmit(newPassword);
      // Reset form
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
    }
  };

  const handleCancel = () => {
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    onCancel();
  };

  const footer = (
    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
      <Button
        label="ביטול"
        icon="pi pi-times"
        onClick={handleCancel}
        className="p-button-text"
        style={{
          color: '#6c757d',
          padding: '8px 16px',
          borderRadius: '6px',
        }}
      />
      <Button
        label="שמור סיסמא"
        icon="pi pi-check"
        onClick={handleSubmit}
        severity="success"
        style={{
          padding: '8px 16px',
          borderRadius: '6px',
        }}
      />
    </div>
  );

  return (
    <Dialog
      header="הגדרת סיסמא חדשה"
      visible={visible}
      onHide={handleCancel}
      style={{ width: '90vw', maxWidth: '600px' }}
      footer={footer}
      modal
      draggable={false}
      resizable={false}
    >
      <div style={{ padding: '24px' }}>
        <div
          style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            border: '1px solid #81c784',
          }}
        >
          <i
            className="pi pi-shield"
            style={{
              fontSize: '2rem',
              color: '#388e3c',
              marginBottom: '12px',
              display: 'block',
            }}
          />
          <p
            style={{
              margin: '0',
              fontSize: '0.95rem',
              color: '#212529',
            }}
          >
            הקוד אומת בהצלחה! כעת תוכל להגדיר סיסמא חדשה
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              color: '#495057',
              fontWeight: '500',
            }}
          >
            סיסמא חדשה
          </label>
          <Password
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrors({ ...errors, newPassword: undefined });
            }}
            toggleMask
            feedback={true}
            placeholder="הזן סיסמא חדשה"
            className={errors.newPassword ? 'p-invalid' : ''}
            /*style={{ width: '400px' }}*/
            inputStyle={{
              width: '504px',
              borderRadius: '6px',
              border: errors.newPassword
                ? '1px solid #dc3545'
                : '1px solid #ced4da',
            }}
            promptLabel="בחר סיסמא"
            weakLabel="חלשה"
            mediumLabel="בינונית"
            strongLabel="חזקה"
          />
          {errors.newPassword && (
            <small
              style={{
                color: '#dc3545',
                fontSize: '0.875rem',
                marginTop: '4px',
                display: 'block',
              }}
            >
              <i
                className="pi pi-exclamation-circle"
                style={{ marginLeft: '4px' }}
              />
              {errors.newPassword}
            </small>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              color: '#495057',
              fontWeight: '500',
            }}
          >
            אימות סיסמא
          </label>
          <Password
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors({ ...errors, confirmPassword: undefined });
            }}
            toggleMask
            feedback={false}
            placeholder="הזן שוב את הסיסמא"
            className={errors.confirmPassword ? 'p-invalid' : ''}
            style={{ width: '100%' }}
            inputStyle={{
              width: '504px',
              borderRadius: '6px',
              border: errors.confirmPassword
                ? '1px solid #dc3545'
                : '1px solid #ced4da',
            }}
          />
          {errors.confirmPassword && (
            <small
              style={{
                color: '#dc3545',
                fontSize: '0.875rem',
                marginTop: '4px',
                display: 'block',
              }}
            >
              <i
                className="pi pi-exclamation-circle"
                style={{ marginLeft: '4px' }}
              />
              {errors.confirmPassword}
            </small>
          )}
        </div>

        <div
          style={{
            marginTop: '20px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
          }}
        >
          <p
            style={{
              margin: '0',
              fontSize: '0.875rem',
              color: '#6c757d',
              lineHeight: '1.5',
            }}
          >
            <i className="pi pi-info-circle" style={{ marginLeft: '6px' }} />
            הסיסמא חייבת להכיל לפחות 6 תווים
          </p>
        </div>
      </div>
    </Dialog>
  );
};
